import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordFacilitySubmission } from './InterestTracker';

interface IntroModalData {
  name: string;
  facility: string;
  phone: string;
  email: string;
  preferredTime: string;
  transportTypes: string[];
}

function ScheduleIntroModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<IntroModalData>({
    name: '',
    facility: '',
    phone: '',
    email: '',
    preferredTime: '',
    transportTypes: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleTransportToggle = (type: string) => {
    setData(prev => ({
      ...prev,
      transportTypes: prev.transportTypes.includes(type)
        ? prev.transportTypes.filter(t => t !== type)
        : [...prev.transportTypes, type]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.name || !data.facility || !data.phone || !data.email) {
      setStatus('error');
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('name', data.name);
      body.append('facility', data.facility);
      body.append('phone', data.phone);
      body.append('email', data.email);
      body.append('preferredTime', data.preferredTime);
      body.append('transportTypes', data.transportTypes.join(', '));

      const res = await fetch('https://readdy.ai/api/form/d6oe9nblgirdouph2q0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const transportOptions = [
    { label: 'Ambulatory', icon: 'ri-walk-line' },
    { label: 'Wheelchair', icon: 'ri-wheelchair-line' },
    { label: 'Gurney', icon: 'ri-hospital-line' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 z-10 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {status === 'success' ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-check-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Request Received!</h3>
            <p className="text-gray-600 text-sm mb-6">
              We'll reach out within one business day to confirm your introduction meeting.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-100 rounded-full">
                  <i className="ri-calendar-line text-yellow-600 text-lg"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Schedule a Facility Introduction</h3>
              </div>
              <p className="text-sm text-gray-500 pl-1">
                We'll come to you. Leave your details and we'll set up a brief in-person or virtual meeting at your convenience.
              </p>
            </div>

            <form
              id="facility-intro-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={data.name}
                  onChange={handleChange}
                  required
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Facility Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="facility"
                  value={data.facility}
                  onChange={handleChange}
                  required
                  placeholder="Facility or organization name"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={data.phone}
                    onChange={handleChange}
                    required
                    placeholder="(602) 555-1234"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={data.email}
                    onChange={handleChange}
                    required
                    placeholder="you@facility.com"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Transport Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Transport Types Needed
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {transportOptions.map(({ label, icon }) => {
                    const selected = data.transportTypes.includes(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleTransportToggle(label)}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          selected
                            ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-yellow-300 hover:bg-yellow-50/50'
                        }`}
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${icon} text-lg`}></i>
                        </div>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {data.transportTypes.length > 0 && (
                  <p className="text-xs text-yellow-600 mt-2 font-medium">
                    Selected: {data.transportTypes.join(', ')}
                  </p>
                )}
                {data.transportTypes.includes('Gurney') && (
                  <div className="mt-3 flex gap-3 bg-yellow-50 border border-yellow-400 rounded-xl px-3 py-2.5">
                    <div className="w-4 h-4 flex items-center justify-center text-yellow-600 flex-shrink-0 mt-0.5">
                      <i className="ri-information-line text-sm"></i>
                    </div>
                    <p className="text-xs text-yellow-700 leading-relaxed">
                      <strong>Gurney transport</strong> requires a two-person crew and specialized vehicle. The base rate is <strong>$275</strong> plus $3.50 per mile. We&apos;ll discuss volume pricing and crew scheduling during your intro meeting.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Best Time to Meet
                </label>
                <select
                  name="preferredTime"
                  value={data.preferredTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select a time preference</option>
                  <option value="Morning (8am–12pm)">Morning (8am–12pm)</option>
                  <option value="Afternoon (12pm–4pm)">Afternoon (12pm–4pm)</option>
                  <option value="Late Afternoon (4pm–6pm)">Late Afternoon (4pm–6pm)</option>
                  <option value="Flexible">Flexible — any time works</option>
                </select>
              </div>

              {status === 'error' && (
                <p className="text-red-500 text-xs">Please fill in all required fields and try again.</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer mt-2"
              >
                {submitting ? 'Sending Request...' : 'Request Introduction Meeting'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function FacilityForm() {
  const navigate = useNavigate();
  const [showIntroModal, setShowIntroModal] = useState(false);

  const [formData, setFormData] = useState({
    facilityName: '',
    city: '',
    contactName: '',
    phone: '',
    email: '',
    weeklyRides: '',
    patientTypes: [] as string[],
    roundTrip: '',
    pickupAddress: '',
    dropoffAddress: '',
    notesForDriver: '',
    travelDestinations: [] as string[],
    currentProviders: '',
    averagePrice: '',
    biggestIssues: [] as string[],
    additionalComments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [patientTypeError, setPatientTypeError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field: 'patientTypes' | 'travelDestinations' | 'biggestIssues', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.facilityName || !formData.contactName || !formData.phone || !formData.email) {
      setSubmitStatus('error');
      return;
    }

    if (formData.patientTypes.length === 0) {
      setPatientTypeError(true);
      return;
    }

    setPatientTypeError(false);
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('facilityName', formData.facilityName);
      formBody.append('city', formData.city);
      formBody.append('contactName', formData.contactName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('weeklyRides', formData.weeklyRides);
      formBody.append('patientTypes', formData.patientTypes.join(', '));
      formBody.append('roundTrip', formData.roundTrip);
      formBody.append('pickupAddress', formData.pickupAddress);
      formBody.append('dropoffAddress', formData.dropoffAddress);
      formBody.append('notesForDriver', formData.notesForDriver);
      formBody.append('travelDestinations', formData.travelDestinations.join(', '));
      formBody.append('currentProviders', formData.currentProviders);
      formBody.append('averagePrice', formData.averagePrice);
      formBody.append('biggestIssues', formData.biggestIssues.join(', '));
      formBody.append('additionalComments', formData.additionalComments);

      const response = await fetch('https://readdy.ai/api/form/d6ocln130mftb86r3i50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        recordFacilitySubmission(formData.city);
        navigate('/thank-you?form=facility');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showIntroModal && <ScheduleIntroModal onClose={() => setShowIntroModal(false)} />}

      <section id="facility-form" className="py-24 bg-gradient-to-b from-slate-100 to-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-500 border border-yellow-500/30 rounded-full px-5 py-2 mb-6">
              <i className="ri-building-line text-lg"></i>
              <span className="font-semibold text-sm whitespace-nowrap">For Healthcare Facilities</span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Facility Transportation Needs Assessment
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6 leading-relaxed">
              If you manage an assisted living community, rehabilitation center, or healthcare facility, we would love to understand your transportation needs so we can design our service to support your residents.
            </p>

            {/* Schedule Intro Button */}
            <button
              onClick={() => setShowIntroModal(true)}
              className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-8 py-4 rounded-xl text-base transition-all shadow-lg hover:shadow-yellow-500/30 cursor-pointer whitespace-nowrap mb-6"
            >
              <i className="ri-calendar-line text-xl"></i>
              Schedule a Facility Introduction
            </button>

            <p className="text-gray-500 text-sm mb-6">Prefer a form? Fill out the assessment below.</p>

            <div className="flex flex-col sm:flex-row items-center gap-3 bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border border-yellow-500/40 rounded-xl px-6 py-4 text-center sm:text-left">
              <i className="ri-vip-crown-fill text-yellow-500 text-2xl flex-shrink-0"></i>
              <p className="text-yellow-500 font-bold text-base leading-snug">
                Founding Facility Partners will receive priority scheduling and preferred launch pricing
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} id="facility-needs-form" data-readdy-form className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-8 md:p-12 shadow-2xl border border-yellow-500/30">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Facility Name <span className="text-yellow-600">*</span>
                </label>
                <input
                  type="text"
                  name="facilityName"
                  value={formData.facilityName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter facility name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  City <span className="text-yellow-600">*</span>
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select city</option>
                  <option value="Sun City">Sun City</option>
                  <option value="Sun City West">Sun City West</option>
                  <option value="Peoria">Peoria</option>
                  <option value="Surprise">Surprise</option>
                  <option value="Goodyear">Goodyear</option>
                  <option value="Glendale">Glendale</option>
                  <option value="Scottsdale">Scottsdale</option>
                  <option value="Other West Valley">Other West Valley</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Contact Name <span className="text-yellow-600">*</span>
                </label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  Phone <span className="text-yellow-600">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Email <span className="text-yellow-600">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                How many residents require transportation each week?
              </label>
              <select
                name="weeklyRides"
                value={formData.weeklyRides}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
              >
                <option value="">Select range</option>
                <option value="1-5">1–5</option>
                <option value="5-10">5–10</option>
                <option value="10-20">10–20</option>
                <option value="20+">20+</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                What types of patients need transportation? <span className="text-yellow-600">*</span>
              </label>
              <div className="space-y-3">
                {['Ambulatory (Walking)', 'Wheelchair', 'Gurney', 'All Three'].map((type) => (
                  <label key={type} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.patientTypes.includes(type)}
                      onChange={() => {
                        handleCheckboxChange('patientTypes', type);
                        setPatientTypeError(false);
                      }}
                      className="w-5 h-5 text-yellow-500 border-slate-300 bg-white rounded focus:ring-2 focus:ring-yellow-500 cursor-pointer"
                    />
                    <span className="text-slate-700">{type}</span>
                  </label>
                ))}
              </div>
              {patientTypeError && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  Please select at least one patient type.
                </p>
              )}
              {(formData.patientTypes.includes('Gurney') || formData.patientTypes.includes('All Three')) && (
                <div className="mt-3 flex gap-3 bg-yellow-50 border border-yellow-400 rounded-xl px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center text-yellow-600 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-yellow-800 mb-0.5">Gurney Transport Note</p>
                    <p className="text-xs text-yellow-700 leading-relaxed">
                      Gurney / stretcher transport requires a <strong>two-person crew</strong> and specially equipped vehicle. The base rate is <strong>$275</strong> plus $3.50 per mile. We&apos;ll discuss volume pricing and crew scheduling when we connect with your facility.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Round Trip */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                Will this be a round trip ride?
              </label>
              <div className="flex gap-4">
                {['Yes', 'No', 'Sometimes'].map((option) => (
                  <label key={option} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="roundTrip"
                      value={option}
                      checked={formData.roundTrip === option}
                      onChange={handleChange}
                      className="w-4 h-4 text-yellow-500 border-slate-300 bg-white focus:ring-2 focus:ring-yellow-500 cursor-pointer"
                    />
                    <span className="text-slate-700 text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pickup & Drop-off Addresses */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Typical Pickup Address
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-yellow-600">
                  <i className="ri-map-pin-2-line text-base"></i>
                </div>
                <input
                  type="text"
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Facility or resident pickup address"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Typical Drop-off Address
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-yellow-600">
                  <i className="ri-map-pin-line text-base"></i>
                </div>
                <input
                  type="text"
                  name="dropoffAddress"
                  value={formData.dropoffAddress}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Clinic, hospital, or destination address"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                Where do most residents travel to?
              </label>
              <div className="space-y-3">
                {['Dialysis', 'Doctor Appointments', 'Rehabilitation', 'Hospital Visits', 'Other'].map((destination) => (
                  <label key={destination} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.travelDestinations.includes(destination)}
                      onChange={() => handleCheckboxChange('travelDestinations', destination)}
                      className="w-5 h-5 text-yellow-500 border-slate-300 bg-white rounded focus:ring-2 focus:ring-yellow-500 cursor-pointer"
                    />
                    <span className="text-slate-700">{destination}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                What transportation providers are you currently using?
              </label>
              <input
                type="text"
                name="currentProviders"
                value={formData.currentProviders}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Current provider names"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Average price currently paid per round trip ride
              </label>
              <select
                name="averagePrice"
                value={formData.averagePrice}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent cursor-pointer"
              >
                <option value="">Select price range</option>
                <option value="$80-$100">$80–$100</option>
                <option value="$100-$120">$100–$120</option>
                <option value="$120-$150">$120–$150</option>
                <option value="$150-$200">$150–$200</option>
                <option value="$200-$250">$200–$250</option>
                <option value="$250-$300">$250–$300</option>
                <option value="$300+">$300+</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                What are the biggest issues you experience with transportation providers?
              </label>
              <div className="space-y-3">
                {['Late pickups', 'No-shows', 'Poor communication', 'Patient handling concerns', 'Cost', 'Other'].map((issue) => (
                  <label key={issue} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.biggestIssues.includes(issue)}
                      onChange={() => handleCheckboxChange('biggestIssues', issue)}
                      className="w-5 h-5 text-yellow-500 border-slate-300 bg-white rounded focus:ring-2 focus:ring-yellow-500 cursor-pointer"
                    />
                    <span className="text-slate-700">{issue}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Tell us about your facility&apos;s transportation challenges (Optional)
              </label>
              <textarea
                name="additionalComments"
                value={formData.additionalComments}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                placeholder="Share any additional details about your transportation needs..."
              ></textarea>
              <p className="text-xs text-slate-500 mt-2">{formData.additionalComments.length}/500 characters</p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                <p className="text-green-700 font-medium">Thank you! We&apos;ll contact you soon about partnership opportunities.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg flex items-center gap-3">
                <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                <p className="text-red-700 font-medium">Something went wrong. Please try again.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-lg font-bold text-lg hover:from-yellow-400 hover:to-yellow-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer border-2 border-yellow-400"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Transportation Needs'}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}